import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, GoogleLogo } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", agency_name: "" });
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null);
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const { refreshOrg, loadClients } = useApp();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const finishLogin = async (token) => {
    localStorage.setItem("celebre_token", token);
    await refreshOrg();
    const clients = await loadClients();
    navigate(clients && clients.length ? "/dashboard" : "/onboarding");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/${mode === "login" ? "login" : "register"}`, form);
      if (data.requires_2fa) {
        setTwofa(data.temp_token);
      } else {
        await finishLogin(data.token);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { temp_token: twofa, code });
      await finishLogin(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-md bg-[hsl(9,69%,62%)] flex items-center justify-center"><Lightning size={15} weight="fill" /></span>
          <span className="font-display font-bold text-xl tracking-tight">Celebre</span>
        </div>
        <div>
          <h1 className="font-display font-bold text-4xl tracking-tighter leading-tight">Create on-brand.<br />Approve with confidence.<br />Post natively.</h1>
          <p className="mt-4 text-slate-400 text-sm max-w-sm leading-relaxed">The AI social media co-pilot for agencies. Humans stay in control — nothing publishes automatically.</p>
        </div>
        <p className="text-[12px] text-slate-500">Studio, not publisher.</p>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm fade-up">
          {twofa ? (
            <form onSubmit={verify2fa} className="space-y-4" data-testid="twofa-form">
              <h2 className="font-display font-bold text-2xl tracking-tight">Two-factor code</h2>
              <p className="text-sm text-slate-500">Enter the 6-digit code from your authenticator app.</p>
              <Input data-testid="twofa-code-input" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
              <Button data-testid="twofa-submit-button" type="submit" disabled={busy || code.length !== 6} className="w-full bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)] rounded-lg">
                {busy ? "Verifying…" : "Verify"}
              </Button>
              <button type="button" data-testid="twofa-back-button" onClick={() => { setTwofa(null); setCode(""); }} className="text-[13px] text-slate-500 hover:text-slate-900 transition-colors">← Back to login</button>
            </form>
          ) : (
          <>
          <h2 className="font-display font-bold text-2xl tracking-tight">{mode === "login" ? "Welcome back" : "Create your agency"}</h2>
          <p className="text-sm text-slate-500 mt-1">{mode === "login" ? "Sign in to your workspace." : "Start with 25 free credits."}</p>

          <button data-testid="google-login-button" onClick={googleLogin}
            className="mt-6 w-full flex items-center justify-center gap-2 border border-slate-200 rounded-lg py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors">
            <GoogleLogo size={17} weight="bold" /> Continue with Google
          </button>
          <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-slate-200" /><span className="text-[11px] text-slate-400 uppercase tracking-widest">or</span><div className="flex-1 h-px bg-slate-200" /></div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <Input data-testid="register-name-input" placeholder="Your name" value={form.name} onChange={set("name")} required />
                <Input data-testid="register-agency-input" placeholder="Agency name" value={form.agency_name} onChange={set("agency_name")} />
              </>
            )}
            <Input data-testid="login-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
            <Input data-testid="login-password-input" type="password" placeholder="Password" value={form.password} onChange={set("password")} required />
            <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full bg-[hsl(9,69%,62%)] hover:bg-[hsl(8,54%,54%)] rounded-lg">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="mt-4 text-[13px] text-slate-500 hover:text-slate-900 transition-colors">
            {mode === "login" ? "No account? Create one" : "Already have an account? Sign in"}
          </button>
          <p className="mt-6 text-[12px] text-slate-400 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">Demo account: <b>demo@celebre.app</b> / <b>Demo123!</b></p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
